import Stack from '@karma/components/Stack';
import { CDN_URL } from '@karma/utils/constants/env';
import { getColor } from '@karma/components/colors';
import useHubSpotForm from 'hooks/useHubSpotForm';
import {
  FormWrapper,
  HeaderContainer,
  HeaderTitle,
  HeaderDescription,
} from 'components/molecules/ModalBodyPartnershipsHubspotForm/ModalBodyPartnershipsHubspotForm.style';

// HubSpot form configuration
const HUBSPOT_CONFIG = {
  PORTAL_ID: '6989004',
  FORM_ID: '12b4efd4-822a-47a7-a130-0b0b782d40d7',
  REGION: 'na1',
  VALIDATION_DELAY: 100,
};

// Mapping categories to icon names
const CATEGORY_ICONS = {
  BEAUTY: 'star-black',
  PETS: 'pets',
  MARKETPLACE: 'marketplace',
  HOME: 'home',
  TRAVEL: 'travel',
  LUXURY: 'luxury',
  'SPORTS & OUTDOOR': 'sports-outdoor',
  FASHION: 'fashion',
  TECH: 'tech',
};

function addIconsToCheckboxes(formContainer) {
  // Find all checkbox labels
  const checkboxLabels = formContainer.querySelectorAll('.hs-form-checkbox label, li.hs-form-checkbox label');

  checkboxLabels.forEach(label => {
    const labelText = label.textContent.trim().toUpperCase();

    // Find matching icon for category
    const iconName = Object.keys(CATEGORY_ICONS).find(
      category => labelText === category || labelText.includes(category),
    );

    if (iconName && CATEGORY_ICONS[iconName]) {
      // Create icon element
      const icon = document.createElement('img');

      icon.src = `${CDN_URL}/images/karma/images/partnerships/icons/hubspot/${CATEGORY_ICONS[iconName]}.svg`;
      icon.alt = `${iconName} icon`;
      icon.className = 'category-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('role', 'img');

      // Insert icon before text
      const textNode = label.firstChild;

      if (textNode) {
        label.insertBefore(icon, textNode);
      } else {
        label.appendChild(icon);
      }
    }
  });
}

function styleRequiredFields(formContainer) {
  // Only handle asterisk wrapping since CSS can't easily select text content
  const requiredInputs = formContainer.querySelectorAll('input[required], textarea[required], select[required]');

  requiredInputs.forEach(input => {
    const inputId = input.getAttribute('id');

    if (inputId) {
      const label = formContainer.querySelector(`label[for="${inputId}"]`);

      if (label) {
        const labelText = label.textContent || label.innerText;

        if (labelText.includes('*')) {
          const span = label.querySelector('span');

          if (span && span.textContent.includes('*')) {
            span.innerHTML = span.innerHTML.replace(/\*/g, ` <span style="color: ${getColor('color7')};">*</span>`);
          } else {
            label.innerHTML = label.innerHTML.replace(/\*/g, ` <span style="color: ${getColor('color7')};">*</span>`);
          }
        }
      }
    }
  });
}

function styleDependentFieldsLegalConsent(formContainer) {
  // Only apply styles for LEGAL_CONSENT fields
  // Find all dependent fields
  const dependentFields = formContainer.querySelectorAll('.hs-dependent-field');

  dependentFields.forEach(field => {
    const legalConsentInput = field.querySelector(
      'input[name*="LEGAL_CONSENT"], textarea[name*="LEGAL_CONSENT"], select[name*="LEGAL_CONSENT"]',
    );

    if (!legalConsentInput) {
      return; // Skip if not LEGAL_CONSENT field
    }

    // Add class for CSS styling - styles are now in CSS
    field.classList.add('legal-consent-field');

    const label = field.querySelector('label');

    if (label) {
      const span = label.querySelector('span');

      // Style asterisk if exists
      if (span && span.textContent.includes('*')) {
        span.innerHTML = span.innerHTML.replace(/\*/g, ` <span style="color: ${getColor('color7')};">*</span>`);
      }
    }
  });
}

function styleHsRichtext(formContainer) {
  // Styles are now handled by CSS
  // This function can be removed or kept for potential future logic
  const hsRichtextFields = formContainer.querySelectorAll('.hs-richtext');

  // Add class if needed for additional styling
  hsRichtextFields.forEach(field => {
    field.classList.add('styled-richtext');
  });
}

function styleSubmitButton(formContainer) {
  const submitButton = formContainer.querySelector('input[type="submit"].hs-button.primary.large');
  const submitContainer = formContainer.querySelector('.hs_submit');

  if (submitButton) {
    submitButton.classList.add('styled-submit-button');

    // Store timeout reference for cleanup
    let validationTimeout = null;

    // Add event listener to style validation messages after submit
    const handleSubmitClick = () => {
      // Clear any existing timeout
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }

      validationTimeout = setTimeout(() => {
        // Find all validation messages and make them red
        const errorMessages = formContainer.querySelectorAll(
          '.hs-error-msg, .hs-error-msgs, .hs-error-msgs li, .hs-main-font-element',
        );

        // Convert NodeList to Array and iterate using for loop
        const errorMessagesArray = Array.from(errorMessages);

        for (let i = 0; i < errorMessagesArray.length; i += 1) {
          errorMessagesArray[i].classList.add('hs-validation-error-red');
        }

        validationTimeout = null;
      }, HUBSPOT_CONFIG.VALIDATION_DELAY);
    };

    submitButton.addEventListener('click', handleSubmitClick);
  }

  if (submitContainer) {
    submitContainer.classList.add('styled-submit-container');
  }
}

function createForm() {
  // Check if HubSpot forms library is loaded
  if (!window.hbspt?.forms?.create) {
    console.error('HubSpot forms library not loaded');

    return;
  }

  try {
    window.hbspt.forms.create({
      portalId: HUBSPOT_CONFIG.PORTAL_ID,
      formId: HUBSPOT_CONFIG.FORM_ID,
      region: HUBSPOT_CONFIG.REGION,
      target: '#hubspotFormContainer',

      onFormReady: () => {
        const formContainer = document.querySelector('#hubspotFormContainer');

        if (formContainer) {
          addIconsToCheckboxes(formContainer);
          styleRequiredFields(formContainer);
          styleDependentFieldsLegalConsent(formContainer);
          styleHsRichtext(formContainer);
          styleSubmitButton(formContainer);
        }
      },
    });
  } catch (error) {
    console.error('Error creating HubSpot form:', error);
  }
}

const ModalBodyPartnershipsHubspotForm = () => {
  useHubSpotForm(createForm);

  return (
    <>
      <HeaderContainer>
        <Stack direction="row" spacing={6} justifyContent="flex-start" alignItems="flex-start" fullWidth>
          <HeaderTitle>Get the Case Study</HeaderTitle>
          <img src={`${CDN_URL}/images/karma/images/partnerships/icons/hubspot/star.svg`} alt="star icon" />
        </Stack>
        <HeaderDescription>Complete the form below to unlock exclusive insights.</HeaderDescription>
      </HeaderContainer>
      <FormWrapper>
        <div id="hubspotFormContainer" />
      </FormWrapper>
    </>
  );
};

export default ModalBodyPartnershipsHubspotForm;
